import type { User } from "context/usersContext";
import { get, post, del, patch, getPictures } from "./api_helper";
import { Pagination } from "pages/Main/Components/AdminPanel/Options";
import { AxiosRequestConfig } from "axios";
import {
  CheckReturnTransferDateRequestDto,
  CheckReturnTransferDateResponse,
  DebateDetailsType,
  DebateProcessingActionType,
  DebateSaveAction,
  DebateStatuses,
  FinishDebateRequestDto,
  GetChatHistoryType,
  PaymentMethodsType,
  ProductPhoto,
  ProductStatusEnum,
  SessionType,
  TransactionPorpouseV2,
} from "./types";
import { UserPropInfo } from "pages/Main/Components/AdminPanel/Users/UserComponent/UserComponent";
import { getQueryFromArray } from "./helper";
import { UploadFile } from "antd";
import { STATUSES } from "pages/Main/Components/AdminPanel/Chats";
import { GetPurposeCategoryEnum } from "./constants";

export type translation = {
  name?: string;
  description?: string;
  languageCode: string;
  value?: string;
};

type value = {
  translations: translation[];
};

type optionType = {
  translations: translation[];
  productGroupId: number;
  type: string;
  select: {
    translations: translation[];
    values: value[];
  };
  includeIntoProductName: boolean;
  includeIntoSearch: boolean;
  numericRange?: { max: number; min: number };
};

export const loginAdmin = (email: string, password: string) => {
  return post(`/auth/internal/login`, { email, password });
};

export const registerAdmin = (email: string, password: string, firstName: string, lastName: string) => {
  return post(`/auth/internal/register`, {
    email,
    password,
    passwordRepeat: password,
    firstName,
    lastName,
    role: "ADMIN",
  });
};

export const getUsers = (localCountry: string) => {
  return get(`/internal/users`, { headers: { "country-iso-code": localCountry } });
};

export const getAccount = (localCountry: string) => {
  return get(`/internal/user`, { headers: { "country-iso-code": localCountry } });
};

export const approveUser = (userId: string, role: string, localCountry: string) => {
  return post(`/internal/user/approve`, { userId, role }, { headers: { "country-iso-code": localCountry } });
};

export const editUser = (user: User, localCountry: string) => {
  return patch(`/internal/user`, user, { headers: { "country-iso-code": localCountry } });
};

export const editUserById = (user: User, id: string, localCountry: string) => {
  return patch(`/internal/user/${id}`, user, { headers: { "country-iso-code": localCountry } });
};

export const assignRole = (userId: string, role: string, localCountry: string) => {
  console.log(role);
  return patch(`/internal/assign-roles/${userId}`, { role }, { headers: { "country-iso-code": localCountry } });
};

export const activateUser = (userId: string, localCountry: string) => {
  return patch(`/internal/activate/${userId}`, { headers: { "country-iso-code": localCountry } });
};

export const deleteUser = (userId: string, localCountry: string) => {
  return del(`/internal/user/${userId}`, { headers: { "country-iso-code": localCountry } });
};

export const createWallet = (storage: string, localCountry: string) => {
  return post(`/internal/wallet`, { storage }, { headers: { "country-iso-code": localCountry } });
};

export const deleteWallet = (localCountry: string) => {
  return del(`/internal/wallet`, { headers: { "country-iso-code": localCountry } });
};

export const createCountry = (iso: string, name: string, languageIds: string[], defaultLanguageId?: number) => {
  return post(`/country`, { iso, name, languageIds, defaultLanguageId });
};

export const getCountry = (localCountry: string) => {
  return get(`/country`, { headers: { "country-iso-code": localCountry } });
};

export const updateCountry = (
  id: string,
  active: boolean,
  name: string,
  languageIds: string,
  localCountry: string,
  defaultLanguageId?: number,
) => {
  return patch(
    `/country/${id}`,
    { active, name, languageIds, defaultLanguageId },
    { headers: { "country-iso-code": localCountry } },
  );
};

export const findCountryById = (id: string, localCountry: string) => {
  return get(`/country/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const deleteCountry = (id: string, localCountry: string) => {
  return del(`/country/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const getContract = (localCountry: string) => {
  return get(`/contract`, { headers: { "country-iso-code": localCountry } });
};

export const mintContract = (jettonAmount: number) => {
  return post(`/contract/mint`, { jettonAmount });
};

export const burnContract = (jettonAmount: number) => {
  return post(`/contract/burn`, { jettonAmount });
};

export const transferContract = (jettonAmount: number, to: string) => {
  return post(`/contract/transfer`, { jettonAmount, to });
};

export const getLanguages = (localCountry: string) => {
  return get(`/language`, { headers: { "country-iso-code": localCountry } });
};

export const createLanguage = (code: string, name: string, localCountry: string) => {
  return post(`/language`, { code, name }, { headers: { "country-iso-code": localCountry } });
};

export const updateLanguage = (id: string, name: string, localCountry: string) => {
  return patch(`/language/${id}`, { name }, { headers: { "country-iso-code": localCountry } });
};

export const deleteLanguage = (id: string, localCountry: string) => {
  return del(`/language/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const getApproveContract = () => {
  return get(`/contract/approve`);
};

export const approveContract = (localCountry: string) => {
  return post(`/contract/approve`, { headers: { "country-iso-code": localCountry } });
};

export const declineContract = (localCountry: string) => {
  return del(`/contract/approve`, { headers: { "country-iso-code": localCountry } });
};

export const getCategories = (localCountry: string, withGroup?: boolean) => {
  const strWithGroup = withGroup ? "?withGroup=true" : "";
  return get(`/category${strWithGroup}`, { headers: { "country-iso-code": localCountry } });
};

export const getCategoryById = (id: string, localCountry: string, withGroup?: boolean) => {
  const strWithGroup = withGroup ? "?withGroup=true" : "";
  return get(`/category/${id}?${strWithGroup}`, { headers: { "country-iso-code": localCountry } });
};

export const getCategoriesBrunchById = (id: string, localCountry: string) => {
  return get(`/category/brunch/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const createCategory = (translations: translation[], localCountry: string, parentId?: number) => {
  return post(
    `/category`,
    { translations, parentId: parentId ? parentId : undefined },
    { headers: { "country-iso-code": localCountry } },
  );
};

export const updateCategory = (translations: translation[], localCountry: string, id: string) => {
  return patch(`/category/${id}`, { translations }, { headers: { "country-iso-code": localCountry } });
};

export const deleteCategory = (id: number, localCountry: string) => {
  return del(`/category/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const updateLevelCategory = (id: string, newParentId: string, localCountry: string) => {
  return patch(
    `/category/level/${id}`,
    { newParentId: +newParentId },
    { headers: { "country-iso-code": localCountry } },
  );
};

export const updateOrderCategory = (id: string, order: string, localCountry: string) => {
  return patch(`/category/order/${id}`, { newOrder: +order }, { headers: { "country-iso-code": localCountry } });
};

export const createGroup = (translations: translation[], categoryIds: number[], localCountry: string) => {
  return post(`/product-groups`, { translations, categoryIds }, { headers: { "country-iso-code": localCountry } });
};

export const getGroups = (localCountry: string) => {
  return get(`/product-groups`, { headers: { "country-iso-code": localCountry } });
};

export const getGroupById = (id: string, localCountry: string) => {
  return get(`/product-groups/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const updateGroupById = (translations: translation[], localCountry: string, id: number) => {
  return patch(`/product-groups/${id}`, { translations }, { headers: { "country-iso-code": localCountry } });
};

export const deleteGroup = (id: string, localCountry: string) => {
  return del(`/product-groups/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const deleteGroupFromCategory = (id: number, categoryId: number, localCountry: string) => {
  return patch(
    `/product-groups/category/remove/${id}`,
    {
      categoryId,
    },
    { headers: { "country-iso-code": localCountry } },
  );
};

export const addGroupToCategory = (id: number, categoryId: number, localCountry: string) => {
  return patch(
    `/product-groups/category/add/${id}`,
    {
      categoryId,
    },
    { headers: { "country-iso-code": localCountry } },
  );
};

export const getGroupOptionSelects = (localCountry: string) => {
  return get(`/option-selects`, { headers: { "country-iso-code": localCountry } });
};

type GetGroupOptions = {
  count: number;
  options: any[];
};
export const getGroupOptions = (
  localCountry: string,
  productGroupId: string | number,
  pagination?: Pagination,
): Promise<GetGroupOptions> => {
  let take = "";
  let skip = "";
  if (pagination) {
    take = pagination?.take ? `&take=${pagination?.take}` : "";
    skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  }

  return get(`/options?${productGroupId ? `productGroupId=${productGroupId}` : ""}${take}${skip}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const createOption = (optionType: optionType, localCountry: string) => {
  return post(`/options`, optionType, { headers: { "country-iso-code": localCountry } });
};

export const updateOption = (id: number, optionType: optionType, localCountry: string) => {
  return patch(`/options/${id}`, optionType, { headers: { "country-iso-code": localCountry } });
};

export const deleteOption = (id: string, localCountry: string) => {
  return del(`/options/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const geyOptionById = (id: number, localCountry: string) => {
  return get(`/options/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const updateOrderOption = (id: string, order: string, localCountry: string) => {
  return patch(`/options/${id}`, { optionOrder: +order }, { headers: { "country-iso-code": localCountry } });
};

export const getProducts = (localCountry: string, pagination: Pagination, statuses?: string, productId?: string) => {
  const status = statuses ? `&statuses=${statuses}` : "";
  const id = productId ? `&id=${productId}` : "";
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "&skip=0";
  return get(`/admin/product?${status}${status}${id}${take}${skip}`, {
    headers: { "country-iso-code": localCountry },
  });
};
export type ProductsV1Status = "Draft" | "Active" | "Moderating" | "Archived" | "Stopped" | "Corrections";
export type SortByForProducts = "createdAt" | "usagePercentage" | "qualityRate" | "moderatedAt" | "";
type GetProductsV1Props = {
  pagination: Pagination;
  cities?: string[];
  categories?: string[];
  searchText: string;
  status: ProductsV1Status;
  sort?: { sortBy: SortByForProducts; sortQuery?: string };
  isBlocked: boolean;
};
type ResponceProducts = {
  count: number;
  products: {
    user: {
      avatar: string;
      countryUserId: string;
      email: string;
      firstName: string;
      id: string;
      lastName: string;
      patronymic: string;
      userRating: number;
      online: boolean;
      lastOnline: string;
      reviews: number;
    };
    categoryName: string;
    createdAt: string;
    blocked: boolean;
    activeComment?: string;
    id: string;
    moderatedAt: string;
    name: string;
    productPhoto: ProductPhoto;
    qualityRate: string;
    usagePercentage: string;
  }[];
};
export const getProductsV1 = (
  { pagination, searchText, status, sort, categories, cities, isBlocked }: GetProductsV1Props,
  localCountry: string,
): Promise<ResponceProducts> => {
  const existCat = categories.join(",");
  const existCities = cities.join(",");
  const cityIds = existCities ? getQueryFromArray(cities, "cities") : "";
  const statusQuery = status ? `&status=${status}` : "";
  const search = searchText ? `&search=${searchText}` : "";
  const sortBy = sort?.sortBy ? `&sortBy=${sort.sortBy}&sortOrder=desc` : "";
  const sortQuery = sort?.sortQuery ? `&sortBy=${sort.sortQuery}` : "";
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  const categoriesQuery = existCat ? getQueryFromArray(categories, "categories") : "";
  const blocked = isBlocked ? "&blocked=true" : "";
  return get(
    `/admin/product/v1?${statusQuery}${cityIds}${search}${sortBy}${sortQuery}${take}${skip}${categoriesQuery}${blocked}`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

export const updateProductStatus = (id: number | string, data, localCountry: string) => {
  return patch(`/admin/product/${id}`, data, { headers: { "country-iso-code": localCountry } });
};

export const blockAd = ({
  blocked,
  localCountry,
  productId,
}: {
  productId: string;
  blocked: boolean;
  localCountry: string;
}) => {
  return post(
    `/admin/product/${productId}/block-moderation`,
    { blocked },
    { headers: { "country-iso-code": localCountry } },
  );
};

export const updateProductStatusV2 = ({
  id,
  localCountry,
  status,
  version,
  comment,
}: {
  id: string;
  version: "current" | "draft";
  status: ProductStatusEnum;
  localCountry: string;
  comment: string;
}) => {
  return post(
    `/admin/product/${id}/status?version=${version}`,
    { status, comment },
    { headers: { "country-iso-code": localCountry } },
  );
};

export type GetVersionsByIdProductType = {
  draft: any;
  current: any;
  changes: string[];
};
export const getVersionsByIdProduct = (id: string, localCountry: string): Promise<GetVersionsByIdProductType> => {
  return get(`/admin/product/${id}/versions`, { headers: { "country-iso-code": localCountry } });
};

export const getProduct = (id: number | string, localCountry: string) => {
  return get(`/admin/product/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const getUserById = (id: string, localCountry: string) => {
  return get(`/account/country/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const createCity = (localCountry: string, city) => {
  return post(`/location/city`, city, { headers: { "country-iso-code": localCountry, "accept-language": "EN" } });
};

export const editCity = (localCountry: string, id: string, address: string, deliveryPrice: number) => {
  return patch(
    `/location/city/${id}`,
    { address, deliveryPrice },
    { headers: { "country-iso-code": localCountry, "accept-language": "EN" } },
  );
};

export const getCityWithGeozones = (localCountry: string, id: string) => {
  return get(`/location/city-info/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const getCities = (localCountry: string, isAll: boolean, pagination?: Pagination) => {
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  return get(`/location/city?${take}${skip}&all=${isAll}`, { headers: { "country-iso-code": localCountry } });
};

export const deleteCity = (localCountry: string, id: string) => {
  return del(`/location/city/${id}`, { headers: { "country-iso-code": localCountry } });
};

export const createGeoZone = (localCountry: string, id: string, geo) => {
  return post(`/location/geozone/${id}`, geo, {
    headers: { "country-iso-code": localCountry, "accept-language": "EN" },
  });
};

export const deleteGeoZone = (localCountry: string, id: string) => {
  return del(`/location/geozone/${id}`, { headers: { "country-iso-code": localCountry, "accept-language": "EN" } });
};

export const autoCompleteCities = (data) => {
  return get(`https://maps.googleapis.com/maps/api/place/autocomplete/json`, {
    "Access-Control-Allow-Origin": "*",
    ...data,
  });
};

export type OtherGetChatsProps = {
  pagination?: Pagination;
  userData?: string;
};
export type ExcludeIncludeUserIdHistoryChat = {
  includeUserId?: string;
  excludeUserId?: string;
};
export const getChats = (
  localCountry: string,
  statuses: string[],
  { pagination, userData = "" }: OtherGetChatsProps,
  { excludeUserId, includeUserId }: ExcludeIncludeUserIdHistoryChat,
) => {
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  const queryStatuses = statuses.map((curr) => `statuses=${curr}`).join("&");
  return get(
    `/support-chats/admin?${queryStatuses}${take}${skip}${userData ? `&userData=${userData}` : ""}${
      includeUserId ? `&sortUserId=${includeUserId}` : ""
    }${excludeUserId ? `&excludeUserId=${excludeUserId}` : ""}`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

export const endSupportChat = (localCountry: string, chatId: string) => {
  return post(`/support-chats/admin/close-request/${chatId}`, {}, { headers: { "country-iso-code": localCountry } });
};

export const startSupportChat = (localCountry: string, chatId: string) => {
  return post(
    `/support-chats/admin/start-serve-request/${chatId}`,
    {},
    { headers: { "country-iso-code": localCountry } },
  );
};

export type ChatMetaType = {
  lastReadMessageId?: number;
  firstUnreadMessageId?: number;
  lastUnreadMessageId?: number;
  unreadCount?: number;
};

export type GetSupportChatResponseType = {
  cursor: number;
  message: any;
  meta: ChatMetaType;
};

export const getSupportChat = ({
  chatId,
  cursor = 0,
  sessionId,
  localCountry,
  limit = 10,
}: GetChatHistoryType): Promise<GetSupportChatResponseType> => {
  // const take = pagination?.take ? `&take=${pagination?.take}` : "";
  // const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";

  return post(
    `/support-chats/admin/getRequestHistory/${chatId}`,
    { cursor, limit: limit ?? 6, sessionId },
    { headers: { "country-iso-code": localCountry, "accept-language": "EN" } },
  );
};

export const getPossibleChangeSupports = (chatId: string, localCountry: string) => {
  return get(`/support-chats/admin/get-possible-change-supports/${chatId}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const postChangeSupport = (chatId: string, supportId: string, localCountry: string) => {
  return post(
    `/support-chats/admin/change-support/${chatId}`,
    {
      supportId,
    },
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

export const getArbitrChat = (cursor = 0, localCountry: string, chatId: string) => {
  return get(`/chats/messages/${chatId}?cursor=${cursor}`, {
    headers: { "country-iso-code": localCountry, "accept-language": "EN" },
  });
};

export const getDebate = (localCountry: string, pagination: Pagination, meMode: boolean, status?: string) => {
  return get(
    `/debate?take=${pagination?.take}&skip=${pagination?.skip}${meMode ? "&meMode=true" : ""}${
      status ? `&status=${status}` : ""
    }`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

export const getDebateDetails = (id: string, localCountry: string): Promise<DebateDetailsType> => {
  return get(`/debate/${id}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const startDebateProcess = (id: string, localCountry: string) => {
  return post(`/debate/start-process/${id}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const finishDebateProcess = (id: string, data: Partial<FinishDebateRequestDto>, localCountry: string) => {
  return post(`/debate/finish/${id}`, data, {
    headers: { "country-iso-code": localCountry },
  });
};

export const postTmpFileChat = (chatId: string, data: FormData, isArbitr: boolean, config?: AxiosRequestConfig) => {
  if (isArbitr) {
    return post(`/chats/tmp-file/${chatId}`, data, config);
  } else {
    return post(`/support-chats/admin/tmp-file/${chatId}`, data, config);
  }
};

export const postFileChat = (chatId: string, data: any, isArbitr: boolean, config?: AxiosRequestConfig) => {
  if (isArbitr) {
    return post(`/chats/files/${chatId}`, data, config);
  } else {
    return post(`/support-chats/admin/files/${chatId}`, data, config);
  }
};
export type AdsMetrics = {
  usage_percentage: number;
  ads_metric: number;
  quality_rate: number;
};
type AdsMetricsProps = {
  categories: string[];
  cities: string[];
  searchText: string;
  isBlocked: boolean;
};

export const getAdsMetrics = (
  { categories, cities, searchText, isBlocked }: AdsMetricsProps,
  localCountry: string,
): Promise<AdsMetrics> => {
  const existCat = categories.join(",");
  const existCities = cities.join(",");
  const cityIds = existCities ? getQueryFromArray(cities, "cities") : "";
  const categoriesQuery = existCat ? getQueryFromArray(categories, "categories") : "";
  const search = searchText ? `&search=${searchText}` : "";
  const blocked = isBlocked ? "&blocked=true" : "";

  return get(`/admin/product/metrics?${cityIds}${categoriesQuery}${search}${blocked}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export type KycAccountStatusType = "verified" | "under_review" | "not-verified" | "declined" | "expired";
export type UserMetrics = {
  debtsAmount: string;
  userCounts: number;
  balanceAmount: string;
  verified_count: number;
  unverified_count: number;
  under_review_count: number;
  declined_count: number;
};
export type UserProps = {
  citiesId: string[];
  statuses: string[];
  searchText: string;
  kyc_search_type: KycAccountStatusType;
};
export const getUserMetrics = (
  { citiesId, searchText, statuses, kyc_search_type }: UserProps,
  localCountry: string,
): Promise<UserMetrics> => {
  const cityIds = citiesId?.length ? `&cityId=${citiesId.join(",")}` : "";
  const status = statuses?.length ? `&status=${statuses.join(",")}` : "";
  const search = searchText ? `&search=${searchText}` : "";
  return get(`/admin/account/metrics?${cityIds}${status}${search}&kyc_search_type=${kyc_search_type}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const getUserAccounts = (
  { citiesId, searchText, statuses, kyc_search_type }: UserProps,
  localCountry: string,
  pagination?: Pagination,
  sort?: { sortBy: string; sortQuery?: string },
): Promise<{ count: number; users: UserPropInfo[] }> => {
  const cityIds = citiesId?.length ? `&cityId=${citiesId.join(",")}` : "";
  const status = statuses?.length ? `&status=${statuses.join(",")}` : "";
  const search = searchText ? `&search=${searchText}` : "";
  const sortBy = sort?.sortBy ? `&sortBy=${sort.sortBy}&sortOrder=desc` : "";
  const sortQuery = sort?.sortQuery ? `&sortBy=${sort.sortQuery}` : "";
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  return get(
    `/admin/account?${cityIds}${status}${search}${sortBy}${sortQuery}${take}${skip}&kyc_search_type=${kyc_search_type}`,
    {
      headers: { "country-iso-code": localCountry, "accept-language": "EN" },
    },
  );
};

export type UserChatSupportType = {
  id: string;
  status: STATUSES;
  unreadCount: number;
};
export type UserDetails = {
  address: string;
  avatar: string;
  createdAt: string;
  defaultCountryLang: string;
  description: string;
  email: string;
  firstName: string;
  frozen: boolean;
  insuranceRate: number;
  lastName: string;
  lastOnlineDate: string;
  lessor: boolean;
  moderationBage: number;
  online: boolean;
  patronymic: string;
  phone: string;
  rating: number;
  supportChatId: string;
  verified: boolean;
  paymentMethods: PaymentMethodsType;
  debt: number;
  balance: number;
  deposit: number;
  supportChat: UserChatSupportType;
};

export const getUserDetails = (userId: string, localCountry: string): Promise<UserDetails> => {
  console.log(localCountry);
  return get(`/admin/account/details/${userId}`, { headers: { "country-iso-code": localCountry } });
};

export const frozeUser = (userId: string, data: { froze: boolean }, localCountry: string) => {
  return post(`/admin/account/froze/${userId}`, data, {
    headers: { "country-iso-code": localCountry },
  });
};

export const getUserAdsMetrics = (
  countryUserId: string,
  localCountry: string,
): Promise<{ adsMetric: number; qualityRate: number; usagePercentage: number }> => {
  return get(`/admin/account/details/lessor/adds/metrics/${countryUserId}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export type UserAdsStatuses = "Moderating" | "Active" | "Stopped" | "Draft" | "Archived";
export type sortByAdsUser = "createdAt" | "usagePercentage" | "qualityRate" | "moderatedAt" | "";
type UserAdsProps = {
  countryUserId: string;
  status: UserAdsStatuses;
  localCountry: string;
  pagination?: Pagination;
  sort?: { sortBy: sortByAdsUser; sortQuery?: string };
};
export const getUserAds = ({
  countryUserId,
  localCountry,
  pagination,
  sort,
  status,
}: UserAdsProps): Promise<{ count: number; products: any[] }> => {
  const sortBy = sort?.sortBy ? `&sortBy=${sort.sortBy}&sortOrder=desc` : "";
  const sortQuery = sort?.sortQuery ? `&sortBy=${sort.sortQuery}` : "";
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  const statusQuery = `&status=${status}`;
  return get(`/admin/account/details/lessor/adds/${countryUserId}?${statusQuery}${sortBy}${sortQuery}${take}${skip}`, {
    headers: { "country-iso-code": localCountry },
  });
};

type Initiator = "lessee" | "lessor";
type UserDealsMetricsProps = {
  countryUserId: string;
  initiator: Initiator;
};
export const getUserDealsMetrics = (
  { countryUserId, initiator }: UserDealsMetricsProps,
  localCountry: string,
): Promise<{ avgPeriodDays: number; dealsCount: number; successRate: number; sumPrice: number }> => {
  return get(`/admin/account/details/deals/metrics/${countryUserId}?initiator=${initiator}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export type UserDealsStatuses = "request" | "booked" | "in_progress" | "completed" | "declined" | "disputed";

export type sortByDealsUser = "period" | "amount" | "dateRequest" | "";
type UserDealsProps = {
  countryUserId: string;
  initiator: Initiator;
  status: UserDealsStatuses;
  pagination?: Pagination;
  sort?: { sortBy: sortByDealsUser; sortQuery?: string };
};

type ResponceUserDeals = {
  count: number;
  deals: {
    avatar: string;
    count: string;
    createdAt: string;
    dateEnd: string;
    dateStart: string;
    firstName: string;
    id: string;
    lastName: string;
    period_days: number;
    productPhoto: ProductPhoto;
    rating: number;
    rentPrice: number;
    productName: string;
    returnTransferDate: string;
    transferStartDate: string;
  }[];
};
export const getUserDeals = (
  { countryUserId, initiator, pagination, sort, status }: UserDealsProps,
  localCountry: string,
): Promise<ResponceUserDeals> => {
  const sortBy = sort?.sortBy ? `&sortBy=${sort.sortBy}&sortOrder=desc` : "";
  const sortQuery = sort?.sortQuery ? `&sortBy=${sort.sortQuery}` : "";
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  const statusQuery = `&status=${status}`;
  return get(
    `/admin/account/details/deals/${countryUserId}?initiator=${initiator}${statusQuery}${sortBy}${sortQuery}${take}${skip}`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

type ItemDealsProps = {
  itemId: string;
  status: UserDealsStatuses;
  pagination?: Pagination;
  sort?: { sortBy: sortByDealsUser; sortQuery?: string };
};
export const getItemDeals = (
  { itemId, pagination, sort, status }: ItemDealsProps,
  localCountry: string,
): Promise<ResponceUserDeals> => {
  const sortBy = sort?.sortBy ? `&sortBy=${sort.sortBy}&sortOrder=desc` : "";
  const sortQuery = sort?.sortQuery ? `&sortBy=${sort.sortQuery}` : "";
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  const statusQuery = `&status=${status}`;
  return get(`/admin/product/deals/${itemId}?${statusQuery}${sortBy}${sortQuery}${take}${skip}`, {
    headers: { "country-iso-code": localCountry },
  });
};

type DetailsMetrics = {
  itemId: string;
};
export const getItemDealsMetrics = (
  { itemId }: DetailsMetrics,
  localCountry: string,
): Promise<{ dealsCount: number; avgPeriodDays: number; successRate: number; sumPrice: number }> => {
  return get(`/admin/product/deals/metrics/${itemId}`, {
    headers: { "country-iso-code": localCountry },
  });
};

type UserReviewsProps = {
  countryUserId: string;
  initiator: Initiator;
  pagination?: Pagination;
  search: string;
  rating?: 0 | 1 | 2 | 3 | 4 | 5 | null;
};

export type ReviewRespond = {
  id: number;
  reviewId: number;
  authorId: string;
  comment: string;
  createdAt: string;
  updatedAt: string;
};
export type Reviews = {
  id: number;
  authorId: string;
  recipientId: string;
  rentId: string;
  rating: string;
  comment: string;
  comments: ReviewRespond[];
  createdAt: string;
  updatedAt: string;
  author: {
    id: string;
    avatar: string;
    firstName: string;
    lastName: string;
    patronymic: string;
  };
};

export type UserReviewsResult = {
  count: number;
  reviews: Reviews[];
};
export const getReviewsOfAdmin = (
  { countryUserId, initiator, rating, search, pagination }: UserReviewsProps,
  localCountry: string,
): Promise<UserReviewsResult> => {
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  const searchQuery = search ? `&search=${search}` : "";
  const ratingQuery = rating ? `&rating=${rating}` : "";
  return get(
    `/admin/account/details/reviews/${countryUserId}?${take}${skip}${searchQuery}${ratingQuery}&initiator=${initiator}`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

export const deleteReview = (id: number, localCountry: string) => {
  return del(`/admin/account/details/reviews/${id}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const deleteProductReview = (id: number, localCountry: string) => {
  return del(`/admin/product/reviews/${id}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const getProductReviews = (id: string, cursor: number, localCountry: string) => {
  return get(`/admin/product/reviews/${id}?cursor=${cursor}&limit=2`, {
    headers: { "country-iso-code": localCountry },
  });
};

export type AdminRentsStatus = "request" | "booked" | "in_progress" | "completed" | "declined" | "disputed";
export type AdminRents = {
  status: AdminRentsStatus;
  cityIds: string[];
  productCategories: string[];
  dateRange: { from: string; to: string };
};

export type ResponceAdminRentsMetrics = {
  amountChangePercentage: string;
  avgPeriodDaysChangePercentage: string;
  countChangePercentage: string;
  currentAmount: string;
  currentAvgPeriodDays: string;
  currentCount: number;
  currentSuccessRate: string;
  currentAvgInsuranceCoefficient: string;
  insuranceCoefficientChangePercentage: string;
  prevAmount: string;
  prevAvgPeriodDays: string;
  prevCount: number;
  prevSuccessRate: string;
  successRateChangePercentage: string;
};
export const getAdminRentsMetrics = (
  { cityIds, productCategories, status, dateRange }: AdminRents,
  localCountry: string,
): Promise<ResponceAdminRentsMetrics> => {
  const citiesIds = cityIds?.join(",").length ? `&cityId=${cityIds.join(",")}` : "";
  const prodCat = productCategories?.join(",")?.length ? `&productCategoryId=${productCategories.join(",")}` : "";
  const dateRangeQuery = dateRange?.from || dateRange?.to ? `&date_from=${dateRange.from}&date_to=${dateRange.to}` : "";

  return get(`/admin/rents/metrics?status=${status}${citiesIds}${prodCat}${dateRangeQuery}`, {
    headers: {
      "country-iso-code": localCountry,
    },
  });
};

export type SortByAdminRents = "period" | "amount" | "dateRequest" | "";
export type SortByDisputes = "created_at" | "period" | "amount" | "paid_out" | "";

type AdminRentsProps = {
  status: AdminRentsStatus;
  cityIds: string[];
  productCategories: string[];
  pagination?: Pagination;
  searchText: string;
  dateRange: { from: string; to: string };
  sort?: { sortBy: SortByAdminRents; sortOrder?: string };
};
export const getAdminRents = (
  { pagination, sort, cityIds, productCategories, searchText, status, dateRange }: AdminRentsProps,
  localCountry: string,
): Promise<any> => {
  const sortBy = sort?.sortBy ? `&sortBy=${sort.sortBy}` : "";
  const sortQuery = sort?.sortBy ? `&sortOrder=${sort.sortOrder || "desc"}` : "";
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  const citiesIds = cityIds?.join(",").length ? `&cityId=${cityIds.join(",")}` : "";
  const prodCat = productCategories?.join(",")?.length ? `&productCategoryId=${productCategories.join(",")}` : "";
  const search = searchText ? `&search=${searchText}` : "";
  const statusQuery = `&status=${status}`;
  const dateRangeQuery = dateRange?.from || dateRange?.to ? `&date_from=${dateRange.from}&date_to=${dateRange.to}` : "";
  return get(
    `/admin/rents/v1?${statusQuery}${sortBy}${sortQuery}${take}${skip}${citiesIds}${prodCat}${search}${dateRangeQuery}`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};
export enum RentDocumentPreviewName {
  RENTAL_AGREEMENT = "RENTAL_AGREEMENT",
  TRANSFER_ACT = "TRANSFER_ACT",
  RETURN_ACT = "RETURN_ACT",
  DISPUTE_RETURN_ACT = "DISPUTE_RETURN_ACT",
  DISPUTE_REPORT = "DISPUTE_REPORT",
}
export type StepData = {
  step: string;
  lessee: {
    action: string;
    date: string;
    temp?: boolean;
  };
  lessor: {
    action: string;
    date: string;
    temp?: boolean;
  };
  documents?: {
    id: string;
    documentType: string;
    documentId: string;
    previewName: RentDocumentPreviewName;
    ownerSide: "LESSEE" | "LESSOR";
    status: "SIGNED";
    languageCode: string; //en, ru etc...
    previewHash: string;
    shortHash: string;
    version: number;
    generatedAt: string;
    signedAt: string;
    sealedAt: string;
  }[];
};
type RentsDetailsResponce = {
  rentId: string;
  dateStart: string;
  dateEnd: string;
  period: number;
  amount: string;
  status: string;
  chatId: string;
  insurance: {
    insuranceType: "PERCENTAGE" | "DEPOSIT";
    deposit: string;
    value: string;
  };
  method: {
    payIn: string;
    payOut: string;
  };
  productInfo: {
    productCategory: string;
    productId: string;
    productName: string;
  };
  lesseeInfo: {
    firstName: string;
    lastName: string;
    id: string;
  };
  lessorInfo: {
    firstName: string;
    lastName: string;
    id: string;
  };
  rentSteps: StepData[];
  returnTransferDate: string;
  transferStartDate: string;
};

export const getRentsDetails = (id: string, localCountry: string): Promise<RentsDetailsResponce> => {
  return get(`/admin/rents/details/${id}`, { headers: { "country-iso-code": localCountry } });
};

export type AdminDisputesStatuses = "request" | "in_progress" | "completed" | "declined";
export type AppSettingResponse = {
  id: string;
  key: string;
  value: {
    [key: string]: any;
  };
};
export const getAppSettings = (localCountry: string): Promise<AppSettingResponse> => {
  return get("/config/app-settings", { headers: { "country-iso-code": localCountry } });
};

type TransactionMetrics = {
  vat: number;
  serviceFee: number;
  bankAmount: number;
  walletAmount: number;
};
type TransactionType = "" | "incoming" | "outgoing";
export type PaymentMethod = "Card" | "Wallet" | "RevolutAccount" | "";
export type PurposePayment =
  | ""
  | "FREEZE_AMOUNT"
  | "CHARGE_SERVICE_INSURANCE"
  | "CHARGE_RENT_LESSOR_BY_LESSEE"
  | "UNFEEZE_AMOUNT"
  | "CHARGE_RENT_LESSOR_BY_SERVICE"
  | "CHARGE_RENT_LESSEE_BY_SERVICE"
  | "CHARGE_DEBT_INTO_SERVICE";

type PaymentMetrics = {
  paymentMethod: PaymentMethod;
  purpose: PurposePayment;
  transactionType: TransactionType;
  search: string;
};
export const getUserPaymentMetrics = (
  clientId: string,
  { paymentMethod, purpose, search, transactionType }: PaymentMetrics,
  localCountry: string,
): Promise<TransactionMetrics> => {
  const paymentMethodQuery = paymentMethod ? `&paymentMethod=${paymentMethod}` : "";
  const purposeQuery = purpose ? getQueryFromArray(purpose.split(","), "purpose") : "";
  const searchQuery = search ? `&search=${search}` : "";
  const transactionTypeQuery = transactionType ? `&transactionType=${transactionType}` : "";

  return get(
    `/admin/account/details/payments/metrics/${clientId}?${searchQuery}${purposeQuery}${paymentMethodQuery}${transactionTypeQuery}`,
    { headers: { "country-iso-code": localCountry } },
  );
};

export const getProductPaymentMetrics = (
  productId: string,
  { paymentMethod, purpose, search, transactionType }: PaymentMetrics,
  localCountry: string,
): Promise<TransactionMetrics> => {
  const paymentMethodQuery = paymentMethod ? `&paymentMethod=${paymentMethod}` : "";
  const purposeQuery = purpose ? getQueryFromArray(purpose.split(","), "purpose") : "";
  const searchQuery = search ? `&search=${search}` : "";
  const transactionTypeQuery = transactionType ? `&transactionType=${transactionType}` : "";
  return get(
    `/admin/product/payments/metrics/${productId}?${searchQuery}${purposeQuery}${paymentMethodQuery}${transactionTypeQuery}`,
    { headers: { "country-iso-code": localCountry } },
  );
};

export type SortByUserPayments = "created_at" | "amount" | "payout_date" | "";
type PaymentsType = {
  transactionType: TransactionType;
  paymentMethod: PaymentMethod;
  purpose: PurposePayment;
  pagination?: Pagination;
  search: string;
  sort?: { sortBy: SortByUserPayments; sortOrder?: string };
};

type TransactionPayment = {
  rentId: string;
  createdAt: string;
  processType: string;
  transactionType: string;
  paymentOperation: string;
  amount: string;
  status: string;
  type: string;
  taxValue: number;
  sender: {
    id: string;
    avatar: string | null;
    rating: string | null;
    lastName: string;
    firstName: string;
  };
  transactionId: string;
};
type UserPaymentsDetails = {
  total: string | number;
  transactions: TransactionPayment[];
};
export const getUserPayments = (
  clientId: string,
  { paymentMethod, purpose, search, transactionType, pagination, sort }: PaymentsType,
  localCountry: string,
): Promise<UserPaymentsDetails> => {
  console.log(purpose);
  const paymentMethodQuery = paymentMethod ? `&paymentMethod=${paymentMethod}` : "";
  const purposeQuery = purpose ? getQueryFromArray(purpose.split(","), "purpose") : "";
  const searchQuery = search ? `&search=${search}` : "";
  const transactionTypeQuery = transactionType ? `&transactionType=${transactionType}` : "";
  const skipQuery = pagination.skip ? `&skip=${pagination.skip}` : "";
  const takeQuery = pagination.take ? `&take=${pagination.take}` : "";
  const sortByQuery = sort.sortBy ? `&sortBy=${sort.sortBy}` : "";
  const sortOrderQuery = sort.sortBy ? `&sortOrder=${sort.sortOrder || "desc"}` : "";
  return get(
    `/admin/account/details/payments/${clientId}?${searchQuery}${purposeQuery}${paymentMethodQuery}${transactionTypeQuery}${skipQuery}${takeQuery}${sortByQuery}${sortOrderQuery}`,
    { headers: { "country-iso-code": localCountry } },
  );
};

export const getProductPayments = (
  productId: string,
  { paymentMethod, purpose, search, transactionType, pagination, sort }: PaymentsType,
  localCountry: string,
) => {
  const paymentMethodQuery = paymentMethod ? `&paymentMethod=${paymentMethod}` : "";
  const purposeQuery = purpose ? getQueryFromArray(purpose.split(","), "purpose") : "";
  const searchQuery = search ? `&search=${search}` : "";
  const transactionTypeQuery = transactionType ? `&transactionType=${transactionType}` : "";
  const skipQuery = pagination.skip ? `&skip=${pagination.skip}` : "";
  const takeQuery = pagination.take ? `&take=${pagination.take}` : "";
  const sortByQuery = sort.sortBy ? `&sortBy=${sort.sortBy}` : "";
  const sortOrderQuery = sort.sortBy ? `&sortOrder=${sort.sortOrder || "desc"}` : "";
  return get(
    `/admin/product/payments/${productId}?${skipQuery}${takeQuery}${paymentMethodQuery}${purposeQuery}${searchQuery}${transactionTypeQuery}${sortByQuery}${sortOrderQuery}`,
    { headers: { "country-iso-code": localCountry } },
  );
};

export type RateLimits = {
  ku: {
    min: number;
    max: number;
  };
  kp: {
    min: number;
    max: number;
  };
  kc: {
    min: number;
    max: number;
  };
  kt: {
    min: number;
    max: number;
  };
  m: number;
};
export const getInsuranceRatioLimits = (localCountry: string): Promise<RateLimits> => {
  return get("/insurance/ratio-limits", { headers: { "country-iso-code": localCountry } });
};

export const updateInsuranceRatioLimits = (rateLimits: RateLimits, localCountry: string): Promise<boolean> => {
  return post("/insurance/ratio-limits", rateLimits, { headers: { "country-iso-code": localCountry } });
};

export type SortInsuranceBy = "created_at" | "amount" | "";
export type InusranceStatuses = "" | "incoming" | "outgoing";
export type InsuranceType = "DEPOSIT" | "PERCENTAGE" | "";
type GetInsuranceProps = {
  pagination: Pagination;
  cities?: string[];
  insuranceType?: InsuranceType[];
  searchText: string;
  dateEnd?: string;
  dateStart?: string;
  processType: InusranceStatuses;
  sort?: { sortBy: SortInsuranceBy; sortQuery?: string };
};
export const getInsurance = (
  { pagination, searchText, processType, sort, insuranceType, cities, dateEnd, dateStart }: GetInsuranceProps,
  localCountry: string,
): Promise<any> => {
  const existInsuranceType = insuranceType.join(",");
  const existCities = cities.join(",");
  const cityIds = existCities ? `&cityId=${existCities}` : "";
  const statusQuery = processType ? `&processType=${processType}` : "";
  const search = searchText ? `&search=${searchText}` : "";
  const sortBy = sort?.sortBy ? `&sortBy=${sort.sortBy}&sortOrder=desc` : "";
  const sortQuery = sort?.sortQuery ? `&sortBy=${sort.sortQuery}` : "";
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  const dateEndQ = dateEnd ? `&dateTo=${dateEnd}` : "";
  const dateFromQ = dateStart ? `&dateFrom=${dateStart}` : "";
  const categoriesQuery = existInsuranceType && insuranceType.length < 2 ? `&insuranceType=${existInsuranceType}` : "";
  return get(
    `/insurance?${statusQuery}${cityIds}${search}${sortBy}${sortQuery}${take}${skip}${categoriesQuery}${dateEndQ}${dateFromQ}`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

type InsuranceMetrics = {
  bankAmount: {
    value: number;
    change?: number;
  };
  walletAmount: {
    value: number;
    change?: number;
  };
  bankDepositAmount: {
    value: number;
    change?: number;
  };
  walletDepositAmount: {
    value: number;
    change?: number;
  };
  payOutRate: {
    value: number;
    change?: number;
  };
  depositRate?: {
    value: number;
    change?: number;
  };
};
export const getInsuranceMetrics = (
  {
    searchText,
    processType,
    insuranceType,
    cities,
    dateEnd,
    dateStart,
  }: Omit<GetInsuranceProps, "sort" | "pagination">,
  localCountry: string,
): Promise<InsuranceMetrics> => {
  const existInsuranceType = insuranceType.join(",");
  const existCities = cities.join(",");
  const cityIds = existCities ? `&cityId=${existCities}` : "";
  const statusQuery = processType ? `&processType=${processType}` : "";
  const search = searchText ? `&search=${searchText}` : "";
  const dateEndQ = dateEnd ? `&dateTo=${dateEnd}` : "";
  const dateFromQ = dateStart ? `&dateFrom=${dateStart}` : "";
  const categoriesQuery = existInsuranceType && insuranceType.length < 2 ? `&insuranceType=${existInsuranceType}` : "";
  return get(`/insurance/metrics?${statusQuery}${cityIds}${search}${categoriesQuery}${dateEndQ}${dateFromQ}`, {
    headers: { "country-iso-code": localCountry },
  });
};

type DebateMetricsPropsType = {
  searchText: string;
  dateFrom?: string;
  dateTo?: string;
  cityId?: string[];
  insuranceType?: InsuranceType[];
  debateStatus: string;
};

type DebateMetricsReturnType = {
  disputes: string;
  disputes_change: string | null;
  amount: number;
  amount_change: number | null;
  payout_rate: number;
  payout_rate_percent_change: number | null;
};
export const getDebateMetrics = (
  { debateStatus, searchText, cityId, dateFrom, dateTo, insuranceType }: DebateMetricsPropsType,
  localCountry: string,
): Promise<DebateMetricsReturnType> => {
  const existInsuranceType = insuranceType.join(",");
  const existCities = cityId.join(",");
  const cityIds = existCities ? `&cityId=${existCities}` : "";
  const search = searchText ? `&search=${searchText}` : "";
  const dateEndQ = dateTo ? `&dateTo=${dateTo}` : "";
  const dateFromQ = dateFrom ? `&dateFrom=${dateFrom}` : "";
  const insurance = existInsuranceType && insuranceType.length < 2 ? `&insurance=${existInsuranceType}` : "";
  const debateStatusQ = `&debateStatus=${debateStatus}`;
  return get(`/debate/metrics?${cityIds}${search}${dateEndQ}${dateFromQ}${insurance}${debateStatusQ}`, {
    headers: { "country-iso-code": localCountry },
  });
};

type DebateTableType = {
  pagination: Pagination;
  cityId?: string[];
  insuranceType?: InsuranceType[];
  searchText: string;
  dateFrom?: string;
  dateTo?: string;
  debateStatus: string;
  sort?: { sortBy: SortByDisputes; sortQuery?: string };
};

type DebateMainInfoType = {
  id: string;
  createdAt: string;
  rentId: string;
  insuranceType: InsuranceType;
  resolvedAt: string;
  status: DebateStatuses;
  period: number;
  paidOut: number;
  product: {
    id: string;
    name: string;
    price: number;
    photos: ProductPhoto[];
  };
  rentDateEnd: string;
  rentDateStart: string;
};

type DebateTableResultType = {
  debates: DebateMainInfoType[];
  totalCount: string;
};
export const getDebates = (
  { pagination, searchText, cityId, dateFrom, dateTo, debateStatus, insuranceType, sort }: DebateTableType,
  localCountry: string,
): Promise<DebateTableResultType> => {
  const existInsuranceType = insuranceType.join(",");
  const existCities = cityId.join(",");
  const cityIds = existCities ? `&cityId=${existCities}` : "";
  const search = searchText ? `&search=${searchText}` : "";
  const sortBy = sort?.sortBy ? `&sortBy=${sort.sortBy}&sortOrder=desc` : "";
  const sortQuery = sort?.sortQuery ? `&sortBy=${sort.sortQuery}` : "";
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  const dateEndQ = dateTo ? `&dateTo=${dateTo}` : "";
  const dateFromQ = dateFrom ? `&dateFrom=${dateFrom}` : "";
  const insurance = existInsuranceType && insuranceType.length < 2 ? `&insurance=${existInsuranceType}` : "";
  const debateStatusQ = `&debateStatus=${debateStatus}`;

  return get(
    `/debate/v2?${cityIds}${skip}${take}${search}${dateFromQ}${dateEndQ}${insurance}${sortBy}${sortQuery}${debateStatusQ}`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

export const joinArbitrToDebateChat = (debateId: string, localCountry: string) => {
  return post(`/debate/join-arbitr-to-debate-chat/${debateId}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const cancelDebate = (debateId: string, localCountry: string) => {
  return post(`/debate/cancel/${debateId}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const cancelRentOfDebate = (rentId: string, localCountry: string) => {
  return post(`/debate/rent-cancel/${rentId}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const cancelRent = (rentId: string, localCountry: string) => {
  return post(
    `/admin/rents/cancel/${rentId}`,
    {},
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

export const debateProcessingAction = (
  debateId: string,
  body: DebateProcessingActionType,
  localCountry: string,
): Promise<boolean> => {
  return post(`/debate/processing-action/${debateId}`, body, {
    headers: { "country-iso-code": localCountry },
  });
};

export const checkReturnTransferDate = (
  debateId: string,
  body: CheckReturnTransferDateRequestDto,
  localCountry: string,
): Promise<CheckReturnTransferDateResponse> => {
  return post(`/debate/processing-action/${debateId}/check-return-transfer-date`, body, {
    headers: { "country-iso-code": localCountry },
  });
};

export const debateSaveAction = (
  debateId: string,
  body: Partial<DebateSaveAction>,
  localCountry: string,
): Promise<boolean> => {
  return post(`/debate/save-action/${debateId}`, body, {
    headers: { "country-iso-code": localCountry },
  });
};

export const postFileDebate = async (debateId: string, file: UploadFile, localCountry) => {
  const formData = new FormData();
  formData.append("file", file.originFileObj as File);
  return post(`debate/attache-debate-report/${debateId}`, formData, {
    headers: { "country-iso-code": localCountry },
  });
};

// ===== Debate Report (mock API) =====
// Документация: GET/POST/PATCH /debate/report/:id, POST /photo, POST /generate, GET /document.
// Backend сейчас mock: данные не сохраняются, PDF всегда один и тот же.

export type ReportSectionKey = "lessor" | "renter" | "additional" | "resolution";

export type ReportSection = {
  text?: string;
  photos?: string[];
};

export type DebateReportForm = Partial<Record<ReportSectionKey, ReportSection>>;

export type GetDebateReportResponse = DebateReportForm & {
  reportUrl: string;
  status?: string;
};

export type GenerateDebateReportBody = DebateReportForm;

export const getDebateReport = (debateId: string, localCountry: string): Promise<GetDebateReportResponse> => {
  return get(`/debate/report/${debateId}`, {
    headers: { "country-iso-code": localCountry },
  });
};

export const createDebateReport = (debateId: string, body: DebateReportForm, localCountry: string) => {
  return post(`/debate/report/${debateId}`, body, {
    headers: { "country-iso-code": localCountry },
  });
};

export const updateDebateReport = (debateId: string, body: DebateReportForm, localCountry: string) => {
  return patch(`/debate/report/${debateId}`, body, {
    headers: { "country-iso-code": localCountry },
  });
};

export const uploadDebateReportPhoto = (
  debateId: string,
  file: File,
  section: ReportSectionKey,
  localCountry: string,
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("section", section);
  return post(`/debate/report/${debateId}/photo`, formData, {
    headers: { "country-iso-code": localCountry },
  });
};

export const generateDebateReport = (debateId: string, body: GenerateDebateReportBody, localCountry: string) => {
  return post(`/debate/report/${debateId}/generate`, body, {
    headers: { "country-iso-code": localCountry },
  });
};

// reportUrl приходит из GET /debate/report/:id (абсолютный url на mock PDF).
export const getDebateReportDocument = (reportUrl: string, localCountry: string) => {
  return getPictures(reportUrl, {
    headers: { "country-iso-code": localCountry },
  });
};

// photoPath приходит из GET /debate/report/:id (section.photos). Отдаётся с бэка только с JWT, поэтому грузим через blob.
export const getDebateReportPhoto = (photoPath: string, localCountry: string) => {
  return getPictures(photoPath, {
    headers: { "country-iso-code": localCountry },
  });
};

// Репорт считается существующим по факту наличия reportUrl.
export const hasDebateReport = (report?: GetDebateReportResponse | null): boolean => {
  return !!report?.reportUrl;
};

// В отличие от hasDebateReport (наличие сгенерированного PDF), тут проверяем есть ли уже заполненные данные (текст/фото) в секциях.
export const hasDebateReportData = (report?: GetDebateReportResponse | null): boolean => {
  const sections: ReportSectionKey[] = ["resolution", "lessor", "renter", "additional"];
  return sections.some((key) => {
    const section = report?.[key];
    return !!section?.text || (section?.photos?.length ?? 0) > 0;
  });
};

export type SortTransactionsBy = "created_at" | "amount" | "";
export type TransactionsType = "" | "incoming" | "outgoing";
export type TransactionsStatusType = "" | "CREATED" | "outgoing";
export enum MapTransactionStatusForClient {
  Completed = "completed",
  Funded = "funded", // - AUTHORISED
  Holded = "holded", // это для транзакций типа FREEZE_AMOUNT
  Unholded = "unholded", // это для транзакций типа UNFREEZE_AMOUNT
  Failed = "failed", // CANCELLED/FAILED/EXPIRED
  Pending = "pending", // CREATED/PENDING/IN_PROGRESS/PAYOUT_PENDING/MANUAL_REVIEW_REQUIRED
  Refunded = "refunded",
}
type GetTransactionsProps = {
  pagination: Pagination;
  sort?: { sortBy: SortInsuranceBy; sortQuery?: "asc" | "desc" };
  cities?: string[];
  paymentMethod: PaymentMethod[];
  purpose: GetPurposeCategoryEnum | "";
  searchText: string;
  dateEnd?: string;
  dateStart?: string;
  transactionsType: TransactionsType;
  transactionStatus?: MapTransactionStatusForClient;
};
export enum TransactionAction {
  UNHOLD = "unhold",
  RETRY = "retry",
  RECREATE_PAYMENT = "recreate_payment",
  COMPLETE = "complete",
  CANCEL = "cancel",
}
export type UserInfoTransactionsType = {
  name: string;
  rating: number;
  avatar: string;
  lastOnline: string;
  online: boolean;
  review_count: number;
};
export enum ProcessType {
  INCOMING = "incoming",
  OUTGOING = "outgoing",
}
export type GetTransactionsResultType = {
  totalCount: number;
  transactions: {
    amount: number;
    availableActions: TransactionAction[];
    createdAt: string;
    paymentMethod: "Wallet" | "Card";
    user: UserInfoTransactionsType;
    sender: UserInfoTransactionsType;
    receiver: UserInfoTransactionsType;
    processType: ProcessType;
    transactionType: TransactionPorpouseV2;
    rentId: string;
    paymentOperation?: string;
    status: MapTransactionStatusForClient;
    taxValue: number;
    serviceFee: number;
    transactionId: string;
    purposeCategory: GetPurposeCategoryEnum;
  }[];
};
export const getTransactions = (
  {
    pagination,
    searchText,
    transactionsType,
    purpose,
    paymentMethod,
    sort,
    cities,
    dateEnd,
    dateStart,
  }: GetTransactionsProps,
  localCountry: string,
): Promise<GetTransactionsResultType> => {
  const existCities = cities.join(",");
  const existPaymentMethods = paymentMethod.join(",");
  const paymentMethodsQ = existPaymentMethods ? `&paymentMethod=${existPaymentMethods}` : "";
  const cityIds = existCities ? `&cityId=${existCities}` : "";
  const search = searchText ? `&search=${searchText}` : "";
  const purposeQuery = purpose ? getQueryFromArray(purpose.split(","), "purpose") : "";
  const sortBy = sort?.sortBy ? `&sortBy=${sort.sortBy}&sortOrder=desc` : "";
  const sortQuery = sort?.sortQuery ? `&sortBy=${sort.sortQuery}` : "";
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  const dateEndQ = dateEnd ? `&dateTo=${dateEnd}` : "";
  const dateFromQ = dateStart ? `&dateFrom=${dateStart}` : "";
  const transactionsTypeQ = transactionsType ? `&transactionType=${transactionsType}` : "";
  return get(
    `/transactions?${paymentMethodsQ}${cityIds}${search}${sortBy}${sortQuery}${take}${skip}${transactionsTypeQ}${dateEndQ}${dateFromQ}${purposeQuery}`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

type TransactionMetricsType = {
  bank_amount: number;
  wallet_amount: number;
  vat: number;
  service_fee: number;
  bank_amount_change: number;
  service_fee_change: number;
  vat_change: number;
  wallet_amount_change: number;
  gas: number;
  in_use: number;
  in_use_change: number;
  gas_change: number;
};
export const getTransactionMetrics = (
  {
    searchText,
    cities,
    dateEnd,
    dateStart,
    paymentMethod,
    purpose,
    transactionsType,
  }: Omit<GetTransactionsProps, "sort" | "pagination">,
  localCountry: string,
): Promise<TransactionMetricsType> => {
  const existCities = cities.join(",");
  const existPaymentMethods = paymentMethod.join(",");
  const paymentMethodsQ = existPaymentMethods ? `&paymentMethod=${existPaymentMethods}` : "";
  const cityIds = existCities ? `&cityId=${existCities}` : "";
  const search = searchText ? `&search=${searchText}` : "";
  const purposeQuery = purpose ? getQueryFromArray(purpose.split(","), "purpose") : "";
  const dateEndQ = dateEnd ? `&dateTo=${dateEnd}` : "";
  const dateFromQ = dateStart ? `&dateFrom=${dateStart}` : "";
  const transactionsTypeQ = transactionsType ? `&transactionType=${transactionsType}` : "";
  return get(
    `/transactions/metrics?${paymentMethodsQ}${cityIds}${search}${purposeQuery}${dateEndQ}${dateFromQ}${transactionsTypeQ}`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

type GetCryptoFlowBaseType = {
  issue: string;
  inUse: number;
  inUseIndicator: number;
  deltaPercent: number;
  deltaTokens: string;
  balance_tokens: string;
  balance_tons: string;
  balance_eur: string;
  iban: string;
  wallet: string;
};
export const getCryptoFlowBase = (localCountry: string): Promise<GetCryptoFlowBaseType> => {
  return get(`/crypto-flow/base`, {
    headers: { "country-iso-code": localCountry },
  });
};

type GetCryptoFlowMetricsType = {
  amount_eur: string;
  amount_tokens: string;
  gas_tons: string;
  gas_transactions_count: number;
  liquidity_exhaustion_date: string;
  liquidity_tokens: string;
  liquidity_tons: string;
  liquidity_warning: boolean;
  total_eur: string;
  total_tokens: string;
  total_tons: string;
};
export const getCryptoFlowMetrics = (localCountry: string): Promise<GetCryptoFlowMetricsType> => {
  return get(`/crypto-flow/metrics`, {
    headers: { "country-iso-code": localCountry },
  });
};

type GetCryptoFlowTransactionsType = {
  totalCount: number;
  transactions: [
    {
      rentId: string;
      trId?: string;
      amount: string;
      paymentCurrency: "TOKEN" | "EUR";
      balance: string;
      status: "BOOKED";
      plannedPayoutDate: string;
      paidAt: string;
      paymentType: "USER_TO_SERVICE" | "BANK";
      productName: string;
      city: number;
      balanceTon: number;
      lessee: {
        name: string;
        rating?: null;
        avatar: null;
      };
      lessor: {
        name: string;
        rating?: number;
        avatar: string;
        lastOnline: string;
        review_count: number;
        online: boolean;
      };
    },
  ];
};
export const getCryptoFlowTransactions = (
  pagination: Pagination,
  localCountry: string,
  sortBy?: SortByUserPayments,
  dateFrom?: string,
): Promise<GetCryptoFlowTransactionsType> => {
  const take = pagination?.take ? `&take=${pagination?.take}` : "";
  const skip = pagination?.skip ? `&skip=${pagination?.skip}` : "";
  return get(
    `/crypto-flow?${take}${skip}${sortBy ? `&sortBy=${sortBy}` : ""}${sortBy ? "&sortOrder=desc" : ""}${
      dateFrom ? `&dateFrom=${dateFrom}` : ""
    }`,
    {
      headers: { "country-iso-code": localCountry },
    },
  );
};

type UdpateGorupIdType = {
  groupId: number;
  productId: string;
  categoryId: number;
};

export const updateGroupOfAd = (data: UdpateGorupIdType, localCountry: string, version: "current" | "draft") => {
  return post(`/admin/product/group?version=${version}`, data, {
    headers: { "country-iso-code": localCountry },
  });
};

export const updateGroupOfAdV2 = (
  id: string,
  data: Omit<UdpateGorupIdType, "productId">,
  localCountry: string,
  version: "current" | "draft",
) => {
  return post(`/admin/product/${id}/group?version=${version}`, data, {
    headers: { "country-iso-code": localCountry },
  });
};

export const uploadCategoryPhoto = async (
  categoryId: string,
  file: UploadFile,
  localCountry: string,
  onProgress?: (event: { percent: number }) => void,
) => {
  const formData = new FormData();
  formData.append("file", file as unknown as File);

  return post(`category/${categoryId}/photo`, formData, {
    headers: { "country-iso-code": localCountry },
    onUploadProgress: (progressEvent: ProgressEvent) => {
      if (progressEvent.total && onProgress) {
        const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        onProgress({ percent });
      }
    },
  });
};

export const getCategoryPhoto = async (id: number | string, country: string) => {
  return getPictures(`/category/photo/${id}?country=${country}`);
};

export type ReplacePhotoType = {
  productId: string;
  product: {
    photos: [
      {
        original: string;
        preview: string;
        path: string;
      },
    ];
  };
  status: string;
  version: "current" | "draft";
};
export const replacePhoto = (
  id: string,
  version: "current" | "draft",
  oldPhoto: string,
  formData: FormData,
  localCountry: string,
): Promise<ReplacePhotoType> => {
  return post(`/admin/product/${id}/photo/replace?version=${version}&oldPhoto=${oldPhoto}`, formData, {
    headers: { "country-iso-code": localCountry },
  });
};

export type VerificationKycType =
  | "DRAFT"
  | "PENDING"
  | "IN_REVIEW"
  | "APPROVED"
  | "REJECTED"
  | "EXPIRED"
  | "FAILED"
  | "DECLINED";
export type ClientType = "PERSON" | "ENTREPRENEUR" | "BUSINESS";
export type VerificationStatusType =
  | "UNVERIFIED"
  | "KYC_PENDING"
  | "KYC_COMPLETED"
  | "VERIFIED"
  | "REJECTED"
  | "EXPIRED";

export type VerifDataType = {
  person: {
    firstName: string;
    lastName: string;
    dateOfBirth?: string;
    yearOfBirth?: string;
    placeOfBirth?: string;
    nationality?: string;
    citizenship?: string;
    gender?: string;
  };
  address?: string;
  document: {
    type: string;
    country: string;
    validFrom?: string;
    validUntil?: string;
    number?: string;
  };
  vendorData?: string;
};
export type VerificationDocType =
  | "PROOF_OF_ADDRESS"
  | "BUSINESS_CERTIFICATE"
  | "POWER_OF_ATTORNEY"
  | "ARTICLES_OF_ASSOCIATION";
export type BusinessInfoType = {
  companyAddress: string;
  companyName: string;
  nif: string;
};
export type GetUserVerificationDetailsType = {
  userId: string;
  clientType: ClientType;
  verificationStatus: VerificationStatusType;
  kycProfileStatus: VerificationKycType;
  reverifyRequired: boolean;
  businessInfo: BusinessInfoType;
  rejectionReason?: any;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  clientRequirements?: any;
  veriffData: VerifDataType;
  firstName: string;
  lastName: string;
  residentialAddress: string;
  uploadedDocuments: {
    docType: VerificationDocType;
    supportChatMessageId: string; //but id
    uploadedAt: string;
  }[];
  supportChatId: string;
};
export const getUserVerificationDetails = (
  userId: string,
  profileId: string,
  localCountry: string,
): Promise<GetUserVerificationDetailsType> => {
  return get(`/verification/admin/${userId}/details?profileId=${profileId}`, {
    headers: { "country-iso-code": localCountry, "accept-language": "EN" },
  });
};

export type VerificationVersionType = {
  id: string;
  createdAt: string;
  status: VerificationKycType;
  clientType: ClientType;
  isArchived: false;
  expiresAt: string;
};
export type VerificationVersionsType = {
  versions: VerificationVersionType[];
};
export const getUserVerificationVersions = (
  userId: string,
  localCountry: string,
): Promise<VerificationVersionsType> => {
  return get(`/verification/admin/${userId}/versions`, {
    headers: { "country-iso-code": localCountry, "accept-language": "EN" },
  });
};
type VerificationActionType = {
  success: boolean;
  message: string;
  user: {
    businessInfo: BusinessInfoType;
    clientType: ClientType;
    verificationStatus: VerificationStatusType;
  };
};
export const rejectVerification = (
  userId: string,
  reason: string,
  localCountry: string,
): Promise<VerificationActionType> => {
  return post(
    `/verification/admin/reject/${userId}`,
    { reason },
    {
      headers: { "country-iso-code": localCountry, "accept-language": "EN" },
    },
  );
};

export const approveVerification = (userId: string, localCountry: string): Promise<VerificationActionType> => {
  return post(
    `/verification/admin/approve/${userId}`,
    {},
    {
      headers: { "country-iso-code": localCountry, "accept-language": "EN" },
    },
  );
};
export type GetUserSessionsType = {
  sessions: [SessionType];
  cursor: string;
  lastSessionId: string;
};
export const getUserSessions = async (userId: string, localCountry: string): Promise<GetUserSessionsType> => {
  return get(`/support-chats/admin/sessions/client/${userId}`, { headers: { "country-iso-code": localCountry } });
};

export const getSessionActions = async (sessionId: string, action: "TAKE" | "CLOSE") => {
  return post(`/support-chats/admin/sessions/${sessionId}/action`, { action });
};

export const readChatByAdmin = async (chatId: string, messageId: number, localCountry: string) => {
  post(
    `/support-chats/admin/mark-read/${chatId}`,
    { messageId },
    {
      headers: { "country-iso-code": localCountry, "accept-language": "EN" },
    },
  );
};

export type GetSupportChatBadgesType = {
  wait_count: string;
  in_progress_count: string;
  finished_count: string;
}[];
export const getSupportChatBadges = (localCountry: string): Promise<GetSupportChatBadgesType> => {
  return get("/support-chats/admin/metrics", { headers: { "country-iso-code": localCountry } });
};

export const initiateSupportRequest = (userId: string): Promise<boolean> => {
  return post(`/support-chats/admin/initiate-support-request/${userId}`, {});
};

export const retryWithNewPayout = (transactionId: string, localCountry: string): Promise<boolean> => {
  return post(
    `/internal/payouts/${transactionId}/retry-with-new-payout-creation`,
    {},
    { headers: { "country-iso-code": localCountry } },
  );
};

export const softRetryPayout = (transactionId: string, localCountry: string): Promise<boolean> => {
  return post(`/internal/payouts/${transactionId}/soft-retry`, {}, { headers: { "country-iso-code": localCountry } });
};

export const forceCompleteTransaction = (transactionId: string, localCountry: string): Promise<boolean> => {
  return post(
    `/internal/transactions/${transactionId}/force-complete`,
    {},
    { headers: { "country-iso-code": localCountry } },
  );
};

export const forceCancelTransaction = (transactionId: string, localCountry: string): Promise<boolean> => {
  return post(
    `/internal/transactions/${transactionId}/force-cancel`,
    {},
    { headers: { "country-iso-code": localCountry } },
  );
};

export const downloadRentDocument = (rentId: string, documentId: string): Promise<Blob> => {
  return get(`/admin/rents/${rentId}/documents/${documentId}/download`, {
    responseType: "blob",
  });
};
